import { array, option } from 'fp-ts';
import { flow, pipe } from 'fp-ts/function';
import { filter, get, head, isEmpty, isEqual, lt, map, negate, size, sortBy, values } from 'lodash/fp';
import { createSelector } from 'reselect';

import {
    convertToMultiCurrencyAmount,
    createReduceAndSumAmounts,
    multiplyMultiCurrencyAmount,
    sumMultiCurrencyAmounts,
} from '@mews-distributor/common';
import { PricingModeKey } from '@mews/enums';
import { getStringProp } from '@mews/fp';
import { getSelectedEnterpriseDefaultCurrencyCode } from '../../../../selectors/views/selectedEnterprise/selectedEnterprise';
import { ApplicationState } from '../../../../types';
import { WithReservationId } from '../../../../types/selectors/selectorPropTypes';
import { getCategoryAvailableCount } from '../../../../utils/availabilityHelpers';
import { getAvailabilityHash } from '../../../../utils/getAvailabilityHash';
import { getCurrencyCode } from '../../../app/selectors';
import { getSelectedHotelRateGroups, getViewRates } from '../../../hotels/selectors';
import {
    getPreferredOrDefaultCurrencyCode,
    getSelectedEnterprisePaymentGatewayDefaultCurrencyCode,
} from '../../../paymentConfigurations/selectors';
import { getPricingRateTotalPrice } from '../../../pricing/pricingHelpers';
import { getPricing } from '../../../pricing/selectors';
import { getCategories } from '../../../resourceCategories/selectors';
import { getCurrentSellLimits } from '../../../sellLimits/selectors';
import { getServiceAvailabilitiesInLegacyFormat } from '../../../serviceAvailability/selectors';
import { getRateGroupSettlementCurrencyCode } from '../../rateGroups/selectors';
import { getRateRateGroupId } from '../../rates/selectors';
import { getReservationPrices } from '../../reservationPrices/selectors';
import {
    getCurrentReservationGroupId,
    getReservations,
    getResourceOrderAvailableCounts,
    getUniqueRoomRateReservationsCount,
} from './typedSelectors';

export const getReservationsCount = flow(getReservations, size);

export const getViewResourceOrders = createSelector(
    getReservations,
    getCategories,
    getViewRates,
    getPricing,
    getServiceAvailabilitiesInLegacyFormat,
    getResourceOrderAvailableCounts,
    getSelectedHotelRateGroups,
    getUniqueRoomRateReservationsCount,
    getCurrentSellLimits,
    (
        reservations,
        categories,
        rates,
        pricing,
        legacyAvailability,
        resourceOrderAvailableCounts,
        rateGroups,
        uniqueRoomRateReservationsCount,
        sellLimits
    ) =>
        map((reservation) => {
            const {
                roomId,
                startDate,
                endDate,
                hotelId,
                productIds,
                rateId,
                adultCount,
                childCount,
                count,
                occupancyData,
                voucherCode,
                startTimeUtc,
                endTimeUtc,
            } = reservation;
            const room = categories[roomId];

            const legacyAvailableCount = getCategoryAvailableCount({
                availability: legacyAvailability,
                startDate,
                endDate,
                enterpriseId: hotelId,
                voucherCode,
                categoryId: roomId,
                startTimeUtc,
                endTimeUtc,
            });

            const totalReservationRoomsCount = pipe(
                uniqueRoomRateReservationsCount,
                array.findFirst(
                    ({ rateId: resourceOrderRateId, roomId: resourceOrderRoomId }) =>
                        resourceOrderRateId === rateId && resourceOrderRoomId === roomId
                ),
                option.map(({ count: resourceOrderTotalCount }) => resourceOrderTotalCount),
                option.getOrElse(() => 0)
            );
            const sellLimitMaximumAvailability = pipe(
                sellLimits,
                array.findFirst(
                    ({ categoryId: sellLimitCategoryId, rateId: sellLimitRateId }) =>
                        sellLimitCategoryId === roomId && sellLimitRateId === rateId
                ),
                option.chain(({ available }) => option.fromNullable(available))
            );

            const availabilityHash = getAvailabilityHash({
                startDate: startTimeUtc ?? startDate,
                endDate: endTimeUtc ?? endDate,
                enterpriseId: hotelId,
                voucherCode,
            });
            const availableCount =
                legacyAvailableCount != null
                    ? legacyAvailableCount
                    : resourceOrderAvailableCounts[availabilityHash]?.[roomId] ?? 0;

            const sellLimitedAvailableCount = option.isSome(sellLimitMaximumAvailability)
                ? sellLimitMaximumAvailability.value - totalReservationRoomsCount
                : availableCount;

            const totalPrice = getPricingRateTotalPrice({
                pricing,
                categoryId: roomId,
                endDate: endTimeUtc ?? endDate,
                enterpriseId: hotelId,
                rateId,
                voucherCode,
                occupancyData,
                productIds,
                startDate: startTimeUtc ?? startDate,
            });

            const rate = get(rateId, rates);
            const rateGroupId = get('rateGroupId', rate);

            return {
                reservation,
                roomCount: count,
                availableCount: sellLimitedAvailableCount,
                totalPrice,
                extraBedCount: Math.max(0, (adultCount as number) + (childCount as number) - room.normalBedCount),
                room,
                rate,
                rateGroup: get(rateGroupId, rateGroups),
                isInvalid: sellLimitedAvailableCount < 0,
            };
        }, reservations)
);

export const hasReservation = flow(getReservationsCount, lt(0));

export const getMerchantCurrencyCode = (state: ApplicationState) => {
    const preferredCurrencyCode = getEarliestReservationRateGroupCurrencyCode(state);

    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    return preferredCurrencyCode
        ? getPreferredOrDefaultCurrencyCode(state, { preferredCurrencyCode })
        : getSelectedEnterprisePaymentGatewayDefaultCurrencyCode(state, {});
};

export const getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode = createSelector(
    getMerchantCurrencyCode,
    getSelectedEnterpriseDefaultCurrencyCode,
    // eslint-disable-next-line @typescript-eslint/strict-boolean-expressions
    (merchantCurrencyCode, enterpriseDefaultCurrencyCode) => merchantCurrencyCode || enterpriseDefaultCurrencyCode
);

const getPromotedServiceReservationTotalAmount = createSelector(
    getReservationPrices,
    getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode,
    getStringProp('reservationId'),
    (reservationPrices, currencyCode, reservationId) =>
        pipe(
            reservationPrices,
            array.filter((reservation) => reservation.identifier === reservationId),
            array.map(({ totalAmount }) => totalAmount),
            createReduceAndSumAmounts(currencyCode)
        )
);

const getCurrentPromotedServiceReservationTotaPrice = (
    state: ApplicationState,
    { reservationId }: WithReservationId
) => {
    const reservationGroupId = getCurrentReservationGroupId(state);
    return getPromotedServiceReservationTotalAmount(state, { reservationGroupId, reservationId });
};

export const getReservationsTotalPrice = (state: ApplicationState) => {
    const resourceOrders = getViewResourceOrders(state);
    const currencyCode = getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode(state, {});
    return pipe(
        resourceOrders,
        array.map(({ totalPrice, roomCount, reservation }) => {
            if (array.isNonEmpty(reservation.promotedServices)) {
                const promotedServiceReservationsTotalPrice = pipe(
                    reservation.promotedServices,
                    array.map(({ reservationId }) =>
                        getCurrentPromotedServiceReservationTotaPrice(state, {
                            reservationId,
                        })
                    ),
                    createReduceAndSumAmounts(currencyCode)
                );

                return sumMultiCurrencyAmounts(
                    convertToMultiCurrencyAmount(promotedServiceReservationsTotalPrice),
                    multiplyMultiCurrencyAmount(totalPrice, roomCount)
                );
            }
            return multiplyMultiCurrencyAmount(totalPrice, roomCount);
        }),
        array.reduce({}, sumMultiCurrencyAmounts)
    );
};

export const getEarliestReservation = createSelector(getReservations, flow(values, sortBy('startDate'), head));

export const getEarliestReservationRateId = flow(getEarliestReservation, get('rateId'));

const getEarliestReservationRateGroupCurrencyCode = (state: ApplicationState) => {
    const earliestReservationRateId = getEarliestReservationRateId(state);
    const earliestReservationRateGroupId = getRateRateGroupId(state, { rateId: earliestReservationRateId });
    // @ts-expect-error $TSFixMeError: Type 'string | undefined' is not assignable to type 'string'.
    return getRateGroupSettlementCurrencyCode(state, { rateGroupId: earliestReservationRateGroupId });
};

export const shouldShowPropertyCurrency = createSelector(
    getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode,
    getCurrencyCode,
    negate(isEqual)
);

export const hasExceedingResourceOrders = createSelector(
    getViewResourceOrders,
    flow(
        filter(({ availableCount }) => availableCount < 0),
        negate(isEmpty)
    )
);

export const getTotalCostWithSeparatedCurrencyGross = createSelector(
    getReservationsTotalPrice,
    getMerchantCurrencyCodeOrEnterpriseDefaultCurrencyCode,
    (totalCost, currencyCode) => ({
        currencyCode,
        totalCost: get([currencyCode, PricingModeKey.GrossValue], totalCost),
    })
);

export const getReservationsInTrackingFormat = flow(
    getReservations,
    values,
    map(({ count, roomId }) => ({ quantity: count, id: roomId }))
);
